// src/components/organization/ElegirOrganizacion.jsx
//
// La muestra `AuthHandler` cuando alguien pertenece a varias organizaciones y no hay
// ninguna elección válida que aplicar. Va en la MISMA RANURA que ConsentGate y
// OrgResolutionError: una pantalla devuelta EN LUGAR de `<Routes>`, de modo que no se
// monta ninguno de los 50 componentes que dan por hecha una organización resuelta.
//
// Antes de esto se entraba en `userOrgs[0]` —el orden de la consulta de pertenencias del
// servidor, que no significa nada— y, si la organización guardada ya no estaba, se
// sobrescribía la preferencia en silencio.
//
// Estilos en línea y no un .css, igual que OrgResolutionError: es una pantalla completa y
// autocontenida que se ve una vez por sesión; un fichero aparte sólo añadiría un sitio más
// donde mirar. La i18n sí es de verdad (es/en/pt) — OrgResolutionError sigue en castellano
// fijo, que es otra deuda (P2-14) y no se arrastra aquí.
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { signOut } from 'firebase/auth';
import { auth } from '../../firebase';
import { useOrganization } from '../../context/OrganizationContext';
import { formatPlanName } from '../../utils/planHelpers';

export default function ElegirOrganizacion() {
  const { t } = useTranslation('organization');
  const { orgsParaElegir, eleccionGuardadaInvalida, elegirOrganizacion } = useOrganization();
  const [entrando, setEntrando] = useState(null);

  const alElegir = async (orgId) => {
    if (entrando) return;          // dos clics seguidos no arrancan dos conmutaciones
    setEntrando(orgId);
    try {
      await elegirOrganizacion(orgId);
    } catch (err) {
      console.error('Error al elegir organización:', err);
      setEntrando(null);           // se puede reintentar; sin esto la tarjeta queda muerta
    }
  };

  return (
    <div style={e.overlay}>
      <div style={e.card}>
        <h2 style={e.title}>{t('chooseOrg.title')}</h2>
        <p style={e.text}>{t('chooseOrg.subtitle')}</p>

        {eleccionGuardadaInvalida && (
          <p style={e.aviso}>{t('chooseOrg.invalidNotice')}</p>
        )}

        <ul style={e.lista}>
          {orgsParaElegir.map((org) => (
            <li key={org.id}>
              <button
                type="button"
                onClick={() => alElegir(org.id)}
                disabled={!!entrando}
                style={{ ...e.item, ...(entrando === org.id ? e.itemActivo : null) }}
              >
                <span style={e.nombre}>{org.name}</span>
                <span style={e.meta}>
                  <span style={e.rol}>
                    {t(`roles.roleNames.${org.membership?.role}`, org.membership?.role || '')}
                  </span>
                  <span style={e.plan}>{formatPlanName(org.planId)}</span>
                </span>
                {entrando === org.id && <span style={e.entrando}>{t('chooseOrg.entering')}</span>}
              </button>
            </li>
          ))}
        </ul>

        <button type="button" onClick={() => signOut(auth)} style={e.signout}>
          {t('chooseOrg.signOut')}
        </button>
      </div>
    </div>
  );
}

const e = {
  overlay: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1.5rem',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  card: {
    width: '100%',
    maxWidth: 440,
    background: '#fff',
    borderRadius: 14,
    padding: '32px 28px',
    boxShadow: '0 10px 40px rgba(0,0,0,.18)',
    boxSizing: 'border-box',
  },
  title: { margin: '0 0 10px', color: '#111827', fontSize: 22, fontWeight: 700, textAlign: 'center' },
  text: { margin: '0 0 20px', color: '#4B5563', fontSize: 15, lineHeight: 1.6, textAlign: 'center' },
  aviso: {
    margin: '0 0 20px', padding: '10px 12px', borderRadius: 8,
    background: '#FEF3C7', color: '#92400E', fontSize: 14, lineHeight: 1.5,
  },
  lista: { listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 10 },
  item: {
    width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4,
    padding: '14px 16px', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 10,
    cursor: 'pointer', textAlign: 'left', font: 'inherit', boxSizing: 'border-box',
  },
  itemActivo: { background: '#EEF2FF', borderColor: '#667eea' },
  nombre: { color: '#111827', fontSize: 16, fontWeight: 600 },
  meta: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' },
  rol: {
    fontSize: 12, fontWeight: 600, color: '#3730A3', background: '#E0E7FF',
    padding: '2px 8px', borderRadius: 999, textTransform: 'capitalize',
  },
  plan: { fontSize: 12, color: '#6B7280' },
  entrando: { fontSize: 12, color: '#667eea' },
  signout: {
    width: '100%', marginTop: 18, padding: '8px', background: 'transparent',
    color: '#6b7280', border: 'none', fontSize: 13, cursor: 'pointer', textDecoration: 'underline',
  },
};
